import {
  MCP_RESOURCES,
  scopesBelongToMcpResource,
} from "@arti/schemas/mcpResources";
import { type FC, useEffect, useMemo, useState } from "react";

import type { DashboardMcpTokenCreateResponse } from "../../dashboard-client";

import { hasEmbeddingKey } from "../../constants/embeddingReadiness";
import { useApiKeys } from "../../hooks/useApiKeys";
import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { useMcpTokens } from "../../hooks/useMcpTokens";
import { isAdmin } from "../../utils/rbac";
import { Alert } from "../common/Alert";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { EmptyState } from "../common/EmptyState";
import {
  buildSkillMeshConnectionRows,
  buildSkillMeshPendingRow,
  buildSkillMeshSetupSteps,
  getSkillMeshClient,
  getSkillMeshWizardProgress,
  resolveSkillMeshAuthMethod,
  type SkillMeshAuthMethod,
  type SkillMeshClientId,
  type SkillMeshConnectionRow,
  skillMeshTokenScopes,
} from "./skillMeshOnboarding";
import {
  SkillMeshClientPicker,
  SkillMeshConnectionsPanel,
  SkillMeshConnectPanel,
  SkillMeshOverviewPanel,
  type SkillMeshSearchReadiness,
} from "./SkillMeshSetup";

function getSkillMeshEndpoint(): string {
  const { path } = MCP_RESOURCES["skill-mesh"];
  return typeof window === "undefined"
    ? path
    : `${window.location.origin}${path}`;
}

function errorMessage(caughtError: unknown, fallback: string): string {
  return caughtError instanceof Error ? caughtError.message : fallback;
}

export const SkillMeshPanel: FC = () => {
  const auth = useDashboardAuth();
  return (
    <SkillMeshSetupFlow
      key={auth.organizationId ?? "none"}
      canViewApiKeys={isAdmin(auth.orgRole, auth.globalRole)}
      isPlatformAdmin={auth.globalRole === "admin"}
      organizationId={auth.organizationId}
    />
  );
};

const SkillMeshSetupFlow: FC<{
  canViewApiKeys: boolean;
  isPlatformAdmin: boolean;
  organizationId: string | null;
}> = ({ canViewApiKeys, isPlatformAdmin, organizationId }) => {
  const {
    createToken,
    enabled,
    error: loadError,
    grants,
    refresh,
    revokeGrant,
    revokeToken,
    tokens,
  } = useMcpTokens(organizationId, "skill-mesh");
  // Provider keys are admin-only; members don't see the search tile.
  const apiKeys = useApiKeys(canViewApiKeys ? organizationId : null);

  const [clientId, setClientId] = useState<SkillMeshClientId>("claude-code");
  const [preferredMethod, setPreferredMethod] =
    useState<SkillMeshAuthMethod>("oauth");
  const [label, setLabel] = useState("");
  const [write, setWrite] = useState(false);
  const [creating, setCreating] = useState(false);
  const [issuedToken, setIssuedToken] =
    useState<DashboardMcpTokenCreateResponse | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] =
    useState<SkillMeshConnectionRow | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  // Signing in and first use happen in another app; pick them up on return.
  useEffect(() => {
    const handleFocus = () => void refresh();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refresh]);

  const endpoint = useMemo(() => getSkillMeshEndpoint(), []);
  const client = getSkillMeshClient(clientId);
  const method = resolveSkillMeshAuthMethod(client, preferredMethod);
  const now = Date.now();
  const rows = buildSkillMeshConnectionRows({ grants, now, tokens });
  const connections = [...tokens, ...grants];
  const progress = getSkillMeshWizardProgress({
    connected: connections.length > 0,
    used: connections.some((connection) => connection.last_used_at !== null),
  });

  let search: SkillMeshSearchReadiness = "hidden";
  if (canViewApiKeys && !apiKeys.error) {
    if (apiKeys.loading) {
      search = "checking";
    } else {
      search = hasEmbeddingKey(apiKeys.keys) ? "ready" : "missing";
    }
  }

  if (!organizationId) {
    return (
      <EmptyState
        title="No organization selected"
        description="Select an organization to connect Skill Mesh."
      />
    );
  }

  const handleGenerate = async () => {
    setActionError(null);
    setCreating(true);
    try {
      const token = await createToken(
        label.trim() || client.label,
        skillMeshTokenScopes(write),
      );
      setIssuedToken(token);
      setRevealed(false);
      setLabel("");
    } catch (caughtError) {
      setActionError(errorMessage(caughtError, "Failed to create token"));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) {
      return;
    }
    setActionError(null);
    setRevokingId(revokeTarget.id);
    try {
      if (revokeTarget.kind === "token") {
        await revokeToken(revokeTarget.id);
        if (issuedToken?.token_id === revokeTarget.id) {
          setIssuedToken(null);
        }
      } else {
        await revokeGrant(revokeTarget.id);
      }
      setRevokeTarget(null);
    } catch (caughtError) {
      setActionError(errorMessage(caughtError, "Failed to revoke connection"));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <SkillMeshOverviewPanel
        checking={enabled === null && !loadError}
        connectionCount={connections.length}
        enabled={enabled}
        endpoint={endpoint}
        isPlatformAdmin={isPlatformAdmin}
        progress={progress}
        search={search}
      />

      {actionError && <Alert variant="error">{actionError}</Alert>}
      {loadError && <Alert variant="error">{loadError}</Alert>}

      {enabled === true && (
        <>
          <SkillMeshClientPicker selected={client} onSelect={setClientId} />
          <SkillMeshConnectPanel
            client={client}
            creating={creating}
            label={label}
            method={method}
            now={now}
            onGenerate={() => void handleGenerate()}
            onLabelChange={setLabel}
            onMethodChange={setPreferredMethod}
            onToggleReveal={() => setRevealed((current) => !current)}
            onWriteChange={setWrite}
            revealed={revealed}
            steps={buildSkillMeshSetupSteps({
              client: clientId,
              endpoint,
              method,
              revealToken: revealed,
              token: issuedToken?.token ?? null,
            })}
            token={issuedToken}
            write={write}
          />
          <SkillMeshConnectionsPanel
            clientLabel={client.label}
            hasLegacyTokens={tokens.some(
              (token) => !scopesBelongToMcpResource("skill-mesh", token.scopes),
            )}
            onRevoke={setRevokeTarget}
            pendingRow={buildSkillMeshPendingRow({ client, method, write })}
            revokingId={revokingId}
            rows={rows}
          />
        </>
      )}

      <ConfirmationDialog
        isOpen={revokeTarget != null}
        title="Revoke Skill Mesh connection"
        body={
          <div className="space-y-2 text-sm">
            <p>
              Revoke <span className="font-mono">{revokeTarget?.name}</span>?
            </p>
            <p>
              {revokeTarget?.kind === "grant"
                ? "This app loses Skill Mesh access immediately and has to sign in again."
                : "This token stops working immediately. Clients that use it need a new one."}
            </p>
          </div>
        }
        confirmLabel="Revoke"
        onConfirm={() => void handleRevoke()}
        onClose={() => {
          if (!revokingId) {
            setRevokeTarget(null);
          }
        }}
        isProcessing={revokingId != null}
      />
    </div>
  );
};
